import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { Link as RouterLink } from 'react-router-dom';
import BrandLogo from './BrandLogo';

function Copyright() {
  return (
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {'© '}
      {new Date().getFullYear()}
      {' OpenIntake. All rights reserved.'}
    </Typography>
  );
}

export default function Footer() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <Box
      id="contact"
      sx={{
        py: { xs: 6, sm: 8 },
        backgroundColor: '#F7F3EB', // Light Tan
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            gap: { xs: 4, md: 8 },
          }}
        >
          {/* Logo Section */}
          <Box sx={{ flexShrink: 0 }}>
            <BrandLogo />
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 2, maxWidth: 300 }}
            >
              Intake for law firms. OpenIntake answers, qualifies, and follows up on leads by phone, text, email, chat, and social, around the clock.
            </Typography>
            <Box
              component="a"
              href="https://www.ycombinator.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                mt: 2.5,
                textDecoration: 'none',
                '&:hover': { opacity: 0.7 },
              }}
            >
              <Box component="img" src="/yc-logo.svg" alt="Y Combinator" sx={{ width: 16, height: 16 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Backed by Y Combinator
              </Typography>
            </Box>
          </Box>

          {/* Links Sections */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 4, sm: 6, md: 8 },
            }}
          >
            {/* Product Links */}
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                Product
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link
                  component="button"
                  onClick={() => scrollToSection('features')}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    p: 0,
                    '&:hover': { color: '#B5683C' },
                  }}
                >
                  How it works
                </Link>
                <Link
                  component="button"
                  onClick={() => scrollToSection('platform')}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    p: 0,
                    '&:hover': { color: '#B5683C' },
                  }}
                >
                  Platform
                </Link>
                <Link
                  component="button"
                  onClick={() => scrollToSection('how-we-work')}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    p: 0,
                    '&:hover': { color: '#B5683C' },
                  }}
                >
                  Onboarding
                </Link>
                <Link
                  component="button"
                  onClick={() => scrollToSection('faq')}
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    textAlign: 'left',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    p: 0,
                    '&:hover': { color: '#B5683C' },
                  }}
                >
                  FAQs
                </Link>
              </Box>
            </Box>

            {/* Company Links */}
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                Company
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link
                  href="mailto:info@openintake.ai"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    '&:hover': { color: '#B5683C' },
                  }}
                >
                  Contact
                </Link>
                <Link
                  component={RouterLink}
                  to="/privacy-sms"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    '&:hover': { color: '#B5683C' },
                  }}
                >
                  Privacy Policy
                </Link>
                <Link
                  component={RouterLink}
                  to="/terms-sms"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    '&:hover': { color: '#B5683C' },
                  }}
                >
                  Terms of Service
                </Link>
              </Box>
            </Box>

            {/* Support Links */}
            <Box sx={{ minWidth: 120 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                Support
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link
                  href="mailto:sales@openintake.ai"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    '&:hover': { color: '#B5683C' },
                  }}
                >
                  Contact Sales
                </Link>
                <Link
                  href="mailto:support@openintake.ai"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    textDecoration: 'none',
                    '&:hover': { color: '#B5683C' },
                  }}
                >
                  Technical Support
                </Link>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Bottom Bar */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: { xs: 4, sm: 6 },
            mt: { xs: 4, sm: 6 },
            borderTop: '1px solid #E8E2D8',
          }}
        >
          <Copyright />
        </Box>
      </Container>
    </Box>
  );
}
